'use client';

// # Department Switching Overlay
// Full-screen overlay with smooth transitions shown during department switches
// Provides visual feedback and prevents interaction with stale department data

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Loader2 } from 'lucide-react';

interface DepartmentSwitchingOverlayProps {
  isVisible: boolean;
  fromDepartment?: string | null;
  toDepartment?: string | null;
  onComplete?: () => void;
}

export function DepartmentSwitchingOverlay({
  isVisible,
  fromDepartment,
  toDepartment,
  onComplete
}: DepartmentSwitchingOverlayProps) {
  const [phase, setPhase] = useState<'switching' | 'loading' | 'complete'>('switching');

  useEffect(() => {
    if (!isVisible) {
      setPhase('switching');
      return;
    }

    // Phase 1: Switching (0-200ms) - show immediately
    const switchingTimer = setTimeout(() => {
      setPhase('loading');
    }, 200);

    // Phase 2: Loading - stay in loading phase until navigation completes
    // The overlay will stay visible until isVisible becomes false (controlled by dashboard context)
    // We'll show loading phase indefinitely until parent component hides overlay
    
    return () => {
      clearTimeout(switchingTimer);
    };
  }, [isVisible]);

  // # Reset phase when overlay becomes invisible
  useEffect(() => {
    if (!isVisible) {
      setPhase('switching');
    }
  }, [isVisible]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          style={{
            backgroundColor: 'var(--color-bg-overlay, rgba(15, 23, 42, 0.95))',
            backdropFilter: 'blur(8px)',
          }}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: -20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="flex flex-col items-center gap-8 px-6 py-10 rounded-2xl"
            style={{
              backgroundColor: 'var(--color-bg-surface-variant, #f8fafc)',
              border: '1px solid var(--color-border-light, #e2e8f0)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              minWidth: '320px',
              maxWidth: '500px',
            }}
          >
            {/* Lightweight logo area (SVG/spinner only to avoid pulling Three.js on department switch) */}
            <div className="mb-1 w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center relative">
              <Loader2 
                className="w-12 h-12 animate-spin" 
                style={{ color: 'var(--color-component-primary, #3b82f6)' }}
              />
            </div>

            {/* Text Content */}
            <div className="text-center">
              <AnimatePresence mode="wait">
                {phase === 'switching' && (
                  <motion.div
                    key="switching-text"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-2"
                  >
                    <h2 
                      className="text-2xl font-bold"
                      style={{ color: 'var(--color-text-primary, #0f172a)' }}
                    >
                      Switching Department
                    </h2>
                    {fromDepartment && toDepartment && (
                      <div 
                        className="flex items-center justify-center gap-3 text-sm"
                        style={{ color: 'var(--color-text-secondary, #475569)' }}
                      >
                        <span className="opacity-60">{fromDepartment}</span>
                        <span style={{ color: 'var(--color-component-primary, #3b82f6)' }}>→</span>
                        <span className="font-medium">{toDepartment}</span>
                      </div>
                    )}
                  </motion.div>
                )}
                {phase === 'loading' && (
                  <motion.div
                    key="loading-text"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <p style={{ color: 'var(--color-text-muted, #64748b)' }}>
                      Preparing workspace and data...
                    </p>
                  </motion.div>
                )}
                {/* Loading phase shows loading text - no complete phase needed */}
              </AnimatePresence>
            </div>

            {/* Progress Dots */}
            {phase === 'loading' && (
              <div className="flex gap-2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 rounded-full loading-dot"
                    style={{ backgroundColor: 'var(--color-text-primary, #0f172a)' }}
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

